/**
 * SliderAudit — 공개 페이지 슬라이더 초기화 상태 진단 도구.
 *
 * 목적: 홈에서 "일부 swiper 가 초기화되지 않는다" 는 증상을 특정하기 위해, 페이지의
 * 모든 슬라이더 루트를 엔진별로 수집하고 초기화 여부(swiper-initialized / el.swiper /
 * *Bound 플래그)와 0-width·중복 배치 여부를 콘솔 테이블로 리포트한다.
 *
 * 사용:
 *   - URL 에 `?slider-audit=1` 을 붙이면 로드 직후 자동 리포트.
 *   - 언제든 콘솔에서 `window.__sliderAudit()` 호출.
 *
 * 두 엔진:
 *   - identity  : `[data-swiper="..."]` (UiSectionsController / 홈 글로벌 스와이퍼)
 *   - template  : `.no-slider-*`, `.no-main-visual-*`, `.no-about-type6` (컴포넌트 self-init)
 *   - block     : `[data-block-carousel]` — 전용 initializer 없음(데모 스캐폴딩) → 항상 미초기화
 */

interface AuditRow {
  engine: string;
  selector: string;
  initialized: boolean;
  width: number;
  slides: number;
  note: string;
}

function isSwiperInitialized(el: Element | null): boolean {
  if (!el) return false;
  return (
    el.classList.contains("swiper-initialized") ||
    Boolean((el as { swiper?: unknown }).swiper)
  );
}

function findSwiperEl(root: Element): Element | null {
  if (root.classList.contains("swiper")) return root;
  return root.querySelector(".swiper");
}

function hasBoundFlag(root: HTMLElement): boolean {
  return Object.keys(root.dataset).some((key) => /Bound$/.test(key));
}

function collectRows(): AuditRow[] {
  const rows: AuditRow[] = [];

  // Engine 1 — 정체성 섹션 ([data-swiper="..."]). 중복 data-swiper 값 탐지.
  const identity = Array.from(
    document.querySelectorAll<HTMLElement>("[data-swiper]"),
  );
  const counts = new Map<string, number>();
  identity.forEach((el) => {
    const key = el.getAttribute("data-swiper") ?? "";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  identity.forEach((el) => {
    const key = el.getAttribute("data-swiper") ?? "";
    const width = Math.round(el.getBoundingClientRect().width);
    rows.push({
      engine: "identity",
      selector: `[data-swiper="${key}"]`,
      initialized: isSwiperInitialized(el),
      width,
      slides: el.querySelectorAll(".swiper-slide").length,
      note: [
        (counts.get(key) ?? 0) > 1 ? "DUPLICATE data-swiper" : "",
        width === 0 ? "0-width(초기 숨김?)" : "",
      ]
        .filter(Boolean)
        .join("; "),
    });
  });

  // Engine 2 — template 컴포넌트 루트.
  const templateRoots = Array.from(
    document.querySelectorAll<HTMLElement>(
      "[class*='no-slider-'], [class*='no-main-visual-'], .no-about-type6",
    ),
  );
  templateRoots.forEach((root) => {
    const swiperEl = findSwiperEl(root);
    const rootClass =
      (root.className || "")
        .split(/\s+/)
        .find((c) => /^no-(slider|main-visual|about)-/.test(c)) ?? "";
    const width = Math.round(root.getBoundingClientRect().width);
    rows.push({
      engine: "template",
      selector: `.${rootClass}`,
      initialized: swiperEl ? isSwiperInitialized(swiperEl) : hasBoundFlag(root),
      width,
      slides: root.querySelectorAll(".swiper-slide").length,
      note: swiperEl ? "" : "no .swiper child (GSAP/pin 또는 non-swiper)",
    });
  });

  // 전용 initializer 가 없는 데모 캐러셀.
  document
    .querySelectorAll<HTMLElement>("[data-block-carousel]")
    .forEach((el) => {
      rows.push({
        engine: "block",
        selector: "[data-block-carousel]",
        initialized: isSwiperInitialized(el),
        width: Math.round(el.getBoundingClientRect().width),
        slides: el.querySelectorAll(".swiper-slide").length,
        note: "전용 JS initializer 없음(데모 스캐폴딩)",
      });
    });

  return rows;
}

export function runSliderAudit(): AuditRow[] {
  const rows = collectRows();
  const broken = rows.filter((row) => !row.initialized);
  const w = window as unknown as {
    Swiper?: unknown;
    gsap?: unknown;
    ScrollTrigger?: unknown;
  };

  /* eslint-disable no-console */
  console.group(
    `[SliderAudit] Swiper=${Boolean(w.Swiper)} gsap=${Boolean(w.gsap)} ScrollTrigger=${Boolean(
      w.ScrollTrigger,
    )} · roots=${rows.length}, 미초기화=${broken.length}`,
  );
  console.table(rows);
  if (broken.length) {
    console.warn("초기화되지 않은 슬라이더:", broken);
  }
  console.groupEnd();
  /* eslint-enable no-console */

  return rows;
}

export class SliderAudit {
  static init(): void {
    (window as unknown as { __sliderAudit?: () => AuditRow[] }).__sliderAudit =
      runSliderAudit;

    const enabled = /[?&]slider-audit=1\b/.test(window.location.search);
    if (!enabled) return;

    // 컴포넌트 self-init(DOMContentLoaded / template:components-init)이 끝난 뒤 리포트.
    window.requestAnimationFrame(() => {
      window.setTimeout(runSliderAudit, 400);
    });
  }
}
